// App bootstrap — wires every extracted module into a running app.
// Replaces the legacy/app.js monolith as the entry-point behind main.ts.
//
// Structure mirrors the legacy IIFE flow: build the typed `state` object,
// instantiate controllers with deps that observe/mutate `state`, install
// DOM event listeners, then run the init IIFE (wizard vs loadSheet).
//
// Two minor restructurings vs. legacy:
//   1. `pickNextCard`/`pickDirection`/`estimateUserLevel` now take their
//      closure inputs as explicit args (domain modules are pure); the
//      adapter lambdas in SessionDeps thread state.difficulty / state.dirRatio
//      / state.levelFloor through.
//   2. The broken dead-code block at legacy:1556-1565 (referenced
//      undefined closeDiffDropdown/closeLangDropdown etc. but survived
//      only via the browser auto-exposing HTML ids as window globals) is
//      gone — the same mode-dropdown wiring already lives in ui/dropdowns.ts.

import {
  DEFAULT_LEVEL,
  WEBHOOK_SECRET,
  WEBHOOK_URL,
} from "./config/defaults";
import {
  COUNTRY_TO_LOCALE,
  LOCALE_TO_LANG,
  defaultTargetFor,
  type Lang,
  type Locale,
} from "./config/langs";
import {
  DEFAULT_LIST_ID,
  defaultListForTheme,
  getList,
  listsForTheme,
} from "./config/lists";
import { DEFAULT_THEME_ID, THEMES, getTheme } from "./config/themes";
import { emptyTagFilter } from "./config/tags";
import { GAME_MODES, type GameModeKey } from "./config/modes";
import { LEGACY_STORAGE_KEYS, STORAGE_KEYS } from "./config/storage-keys";

import { createCardApi, fetchSheetCsv } from "./api/sheet";
import { createSyncController } from "./api/sync";
import { installTtsDelegation } from "./api/tts";

import { parseCards } from "./domain/csv";
import { pickDirection } from "./domain/direction";
import { intervalForBox, pickNextCard } from "./domain/leitner";
import { estimateUserLevel } from "./domain/level";

import { I18N, currentLocale, t } from "./i18n";

import {
  loadDifficulty,
  loadDirRatio,
  loadLevelFloor,
  loadSourceLang,
  loadTagFilter,
  loadTargetLang,
  loadTtsConfig,
  saveTtsConfig,
  clearLegacyTtsAutoplay,
  saveTagFilter,
} from "./state/settings";
import { loadStats, migrateStatsV1ToV2, saveStats } from "./state/stats";
import { clearDefiState, loadDefiState, saveDefiState } from "./state/defi";
import { recordProgressSnapshot } from "./state/progress";
import { computeMasteredCount } from "./domain/session-recap";

import type { AppState, CefrLevel, Direction } from "./types";

import { byId, hide, show } from "./ui/dom";
import { closeHeaderMenu } from "./ui/header-menu";
import { closeIntroModal, localizeIntroModal } from "./ui/intro";
import { showIntroScreen } from "./ui/intro-screen";
import { openTour } from "./ui/tour";
import { createCardModals } from "./ui/card-modals";
import { createSearch } from "./ui/search";
import { createDefiController } from "./ui/defi";
import { createDeckSettings, installDeckSettingsModal } from "./ui/deck-settings";
import { createDeckStats, installDeckStatsModal } from "./ui/deck-stats";
import { createDropdowns } from "./ui/dropdowns";
import { createSessionController } from "./ui/session";
import {
  createSessionEnd,
  type SessionStartSnapshot,
} from "./ui/session-end";
import { createStatsUi } from "./ui/stats-modals";
import { createWizard } from "./ui/wizard";

// ---------------------------------------------------------------- state

function knownLang(): Lang {
  // Source language = the language the prompt is shown in (when not the
  // target). Defaults to the UI locale's lang, but can be overridden via
  // state.sourceLang (set by wizard or profile modal).
  if (state.sourceLang) return state.sourceLang;
  return LOCALE_TO_LANG[currentLocale()] || "fr-FR";
}

// One-shot migration: move any pre-existing flat v1 stats blob into the
// v2 per-list store, under DEFAULT_LIST_ID. Idempotent + no-op on fresh
// installs. Runs before buildInitialState so the first loadStats() call
// sees the already-migrated shape.
migrateStatsV1ToV2();

function buildInitialState(): AppState {
  const loc = currentLocale();
  let targetLang = loadTargetLang(loc);
  let sourceLang = loadSourceLang();
  // Sanitize: target must differ from known lang.
  const known = sourceLang ?? LOCALE_TO_LANG[loc] ?? "fr-FR";
  if (targetLang === known) targetLang = defaultTargetFor(loc);
  // Materialize the implicit "follow locale" source so the profile modal
  // can render a valid pill row on first open.
  if (!sourceLang) {
    sourceLang = LOCALE_TO_LANG[loc] || "fr-FR";
    localStorage.setItem(STORAGE_KEYS.SOURCE, sourceLang);
  }
  const gameModeRaw = localStorage.getItem(STORAGE_KEYS.GAME_MODE) as GameModeKey | null;
  const gameMode: GameModeKey =
    gameModeRaw && GAME_MODES[gameModeRaw] ? gameModeRaw : "libre";
  // Resolve active theme before the list — the list must belong to it.
  // Unknown/stale ids normalize to DEFAULT_THEME_ID via getTheme.
  const currentThemeId = getTheme(
    localStorage.getItem(STORAGE_KEYS.THEME_ID) || DEFAULT_THEME_ID,
  ).id;
  // Resolve currentListId BEFORE loading stats — v2 stats are
  // namespaced per list. Clamp to a deck that actually belongs to
  // currentThemeId: if the persisted listId is from a different theme
  // (user subscribed to a single theme, switched themes elsewhere,
  // etc.), fall back to that theme's default deck.
  const storedListId =
    localStorage.getItem(STORAGE_KEYS.LIST_ID) || DEFAULT_LIST_ID;
  const storedList = getList(storedListId);
  const currentListId =
    storedList.themeId === currentThemeId
      ? storedList.id
      : defaultListForTheme(currentThemeId).id;
  return {
    cards: [],
    stats: loadStats(currentListId),
    current: null,
    direction: { from: "fr-FR", to: "bg-BG" } as Direction,
    targetLang,
    sourceLang,
    dirRatio: loadDirRatio(currentListId),
    difficulty: loadDifficulty(currentListId),
    lastId: null,
    phase: "answer",
    userLevel: DEFAULT_LEVEL,
    userLevelCounts: {},
    levelFloor: loadLevelFloor(currentListId),
    userName: (localStorage.getItem(STORAGE_KEYS.USER) || "").trim(),
    currentThemeId,
    currentListId,
    // loadTagFilter tolerates legacy formats (flat array + pre-per-deck
    // object) and rehomes their contents to DEFAULT_LIST_ID. Immediately
    // re-persist so the localStorage blob upgrades to the new per-deck
    // shape on next read — avoids repeating the migration on every boot.
    tagFilter: (() => {
      const f = loadTagFilter(currentListId);
      saveTagFilter(currentListId, f);
      return f;
    })(),
    // Per-deck TTS config. The default branches on the current list's
    // shape (bilingual → enabled:true, mono → enabled:false). Re-
    // resolve shape from currentListId (not storedList) because we
    // may have clamped away from the persisted id above.
    ttsEnabled: loadTtsConfig(
      currentListId,
      getList(currentListId).shape,
    ).enabled,
    ttsAutoplay: loadTtsConfig(
      currentListId,
      getList(currentListId).shape,
    ).autoplay,
    dirty: new Set<string>(),
    syncStatus: "idle",
    syncTimer: null,
    session: { attempts: 0, correct: 0, errors: [] },
    gameMode,
    defi: null,
  };
}

const state: AppState = buildInitialState();

// One-shot cleanup of the pre-1c global TTS autoplay key. loadTtsConfig
// reads from it as a fallback so existing users' audio setting doesn't
// vanish; this deletes the legacy key now that the per-deck bag is the
// source of truth. Safe across tabs because the new bag already holds
// everything we need.
clearLegacyTtsAutoplay();

/** In-memory snapshot of the user's mastery state at the moment the
 *  current session "started" (first cards loaded, or after a
 *  Reprendre / défi start / deck switch). Compared against the live
 *  stats to compute the session-end recap's "+N cards maîtrisées"
 *  line. Null before the first deck finishes loading. */
let sessionStartSnapshot: SessionStartSnapshot | null = null;

function captureSessionStartSnapshot(): void {
  // Run the CEFR estimator here so the snapshot reflects the user's
  // ACTUAL current level (derived from cumulative stats), not the
  // in-memory state.userLevel value — which may still be the compile-
  // time DEFAULT_LEVEL until nextCard() fires the estimator the first
  // time this session. Without this, loadList's snapshot always
  // captured "A1" for a returning user already at A2+, and the recap
  // then displayed a spurious "A1 → A2" level-up every single session.
  //
  // Also sync state.userLevel / state.userLevelCounts so subsequent
  // reads (stats modal, header pill) see the same value as the
  // snapshot — single source of truth at capture time.
  const est = estimateUserLevel(state.cards, state.stats, state.levelFloor);
  state.userLevel = est.level;
  state.userLevelCounts = est.counts;
  sessionStartSnapshot = {
    masteredCount: computeMasteredCount(state.cards, state.stats),
    userLevel: est.level,
  };
}

// ---------------------------------------------------------------- DOM refs

const cardScreen = byId("cardScreen");
const loader = byId("loader");
const loadErrorEl = byId("loadError");
const loadErrorMsg = byId("loadErrorMsg");
const loadRetryBtn = byId<HTMLButtonElement>("loadRetryBtn");
const submitBtn = byId<HTMLButtonElement>("submitBtn")!;
const dontKnowBtn = byId<HTMLButtonElement>("dontKnowBtn")!;
const hintBtn = byId<HTMLButtonElement>("hintBtn")!;
const skipBtn = byId<HTMLButtonElement>("skipBtn")!;
const flagBtn = byId<HTMLButtonElement>("flagBtn")!;
const addCardBtn = byId<HTMLButtonElement>("addCardBtn")!;
const searchBtn = byId<HTMLButtonElement>("searchBtn")!;
const endSessionBtn = byId<HTMLButtonElement>("endSessionBtn");
const answerInput = byId<HTMLInputElement>("answerInput")!;
const userBtnEl = byId("userBtn");
const userModal = byId("userModal");
const userModalCloseBtn = byId<HTMLButtonElement>("userModalCloseBtn");

function showLoadError(msg: string): void {
  if (loadErrorMsg) loadErrorMsg.textContent = msg;
  hide(loader);
  hide(cardScreen);
  show(loadErrorEl);
}

function clearLoadError(): void {
  hide(loadErrorEl);
  if (loadErrorMsg) loadErrorMsg.textContent = "";
}

// ---------------------------------------------------------------- controllers

// Sync controller first — statsUi's renderSyncDot reads state.syncStatus
// which the sync controller writes through setStatus. Resolve the
// circular dep (sync → renderSyncDot → statsUi.renderSyncDot) via a lazy
// lambda since statsUi is created below.
const sync = createSyncController({
  getWebhookUrl: () => WEBHOOK_URL,
  getWebhookSecret: () => WEBHOOK_SECRET,
  getUserName: () => state.userName,
  getListId: () => state.currentListId,
  getStats: () => state.stats,
  getDirty: () => state.dirty,
  saveStats: (s) => saveStats(state.currentListId, s),
  // Scope remote-stats merge to the active deck. The Apps Script now
  // also sees `list_id` in the payload (so it can route to the right
  // tab), but keeping this client-side filter too is cheap defense
  // against older/misconfigured backends returning everything.
  getKnownKeys: () => new Set(state.cards.map((c) => c.id)),
  setStatus: (s) => {
    state.syncStatus = s;
    statsUi.renderSyncDot();
  },
  getStatus: () => state.syncStatus,
});

const cardApi = createCardApi({
  getWebhookUrl: () => WEBHOOK_URL,
  getWebhookSecret: () => WEBHOOK_SECRET,
  getUserName: () => state.userName,
  getListId: () => state.currentListId,
  getList: () => getList(state.currentListId),
  getLocale: () => currentLocale(),
  getCards: () => state.cards,
  enabled: () => sync.enabled(),
  tr: (key) => t(key),
});

const statsUi = createStatsUi({
  getCards: () => state.cards,
  getStats: () => state.stats,
  getSession: () => state.session,
  getGameMode: () => state.gameMode,
  getDefi: () => state.defi,
  getUserLevel: () => state.userLevel,
  getUserLevelCounts: () => state.userLevelCounts || {},
  getList: () => getList(state.currentListId),
  getUserName: () => state.userName,
  setUserName: (name) => {
    state.userName = name;
  },
  persistUserName: (name) => {
    if (name) localStorage.setItem(STORAGE_KEYS.USER, name);
    else localStorage.removeItem(STORAGE_KEYS.USER);
  },
  getSourceLang: () => state.sourceLang,
  getTargetLang: () => state.targetLang,
  getSyncStatus: () => state.syncStatus,
  syncEnabled: () => sync.enabled(),
  renderLangControls: () => dropdowns.renderLangControls(),
});

const cardModals = createCardModals({
  getCurrent: () => state.current,
  getCards: () => state.cards,
  getUserLevel: () => state.userLevel,
  getSourceLang: () => state.sourceLang,
  getDirection: () => state.direction,
  getList: () => getList(state.currentListId),
  knownLang: () => knownLang(),
  pushCardUpdate: (card, newTexts, reason) =>
    cardApi.pushCardUpdate(card, newTexts, reason),
  pushCardAdd: (texts, tags, level) => cardApi.pushCardAdd(texts, tags, level),
  pushCardSuggest: (srcLang, srcText) => cardApi.pushCardSuggest(srcLang, srcText),
  renderStats: () => statsUi.renderStats(),
});

const session = createSessionController({
  getCards: () => state.cards,
  getStats: () => state.stats,
  getCurrent: () => state.current,
  setCurrent: (c) => {
    state.current = c;
  },
  getDirection: () => state.direction,
  setDirection: (d) => {
    state.direction = d;
  },
  getPhase: () => state.phase,
  setPhase: (p) => {
    state.phase = p;
  },
  getLastId: () => state.lastId,
  setLastId: (id) => {
    state.lastId = id;
  },
  getUserLevel: () => state.userLevel,
  setUserLevel: (l) => {
    state.userLevel = l;
  },
  setUserLevelCounts: (counts) => {
    state.userLevelCounts = counts;
  },
  getGameMode: () => state.gameMode,
  getSession: () => state.session,
  getDefi: () => state.defi,
  getDirty: () => state.dirty,
  getTagFilter: () => state.tagFilter,
  getList: () => getList(state.currentListId),
  resetTagFilter: () => {
    state.tagFilter = emptyTagFilter();
    saveTagFilter(state.currentListId, state.tagFilter);
    dropdowns.renderTagFilterDropdown();
  },
  // Domain helpers are pure — thread the live state fields in.
  pickNextCard: (cards, stats, lastId, userLevel) =>
    pickNextCard(cards, stats, lastId, userLevel, state.difficulty),
  pickDirection: () => {
    const list = getList(state.currentListId);
    // Mono decks don't use Lang-keyed faces — they use deck-specific
    // face keys ("country" / "capital" etc.). Feed pickDirection the
    // deck's actual face keys so the resulting Direction matches
    // card.faces lookups.
    if (list.shape === "mono") {
      const [a, b] = list.faces;
      return pickDirection(a.key, b.key, state.dirRatio);
    }
    return pickDirection(knownLang(), state.targetLang, state.dirRatio);
  },
  estimateUserLevel: (cards, stats) => estimateUserLevel(cards, stats, state.levelFloor),
  intervalForBox,
  endDefi: () => defiController.endDefi(),
  renderStats: () => statsUi.renderStats(),
  saveStats: (s) => saveStats(state.currentListId, s),
  syncEnabled: () => sync.enabled(),
  scheduleSync: () => sync.schedule(),
  isTtsEnabled: () => state.ttsEnabled,
  isTtsAutoplay: () => state.ttsAutoplay,
  persistDefi: () => persistCurrentDefi(),
});

const dropdowns = createDropdowns({
  getDifficulty: () => state.difficulty,
  setDifficulty: (d) => {
    state.difficulty = d;
  },
  getGameMode: () => state.gameMode,
  setGameMode: (m) => {
    state.gameMode = m;
  },
  getTargetLang: () => state.targetLang,
  setTargetLang: (lang) => {
    state.targetLang = lang;
  },
  getSourceLang: () => state.sourceLang,
  setSourceLang: (lang) => {
    state.sourceLang = lang;
  },
  getDirRatio: () => state.dirRatio,
  setDirRatio: (r) => {
    state.dirRatio = r;
  },
  getTagFilter: () => state.tagFilter,
  setTagFilter: (f) => {
    state.tagFilter = f;
  },
  hasCards: () => !!state.cards.length,
  getCards: () => state.cards,
  hasCurrent: () => !!state.current,
  resetModeTransients: () => {
    state.defi = null;
    state.session = { attempts: 0, correct: 0, errors: [] };
    // Game-mode switch means a fresh recap window — re-snapshot.
    if (state.cards.length) captureSessionStartSnapshot();
    // Switching away from défi mode (or restarting it) invalidates
    // any persisted in-progress défi — drop it so the next "défi
    // selected" boot doesn't surface a stale resume prompt.
    clearDefiState();
  },
  getCurrentListId: () => state.currentListId,
  getCurrentThemeId: () => state.currentThemeId,
  getList: () => getList(state.currentListId),
  switchList: (id) => {
    switchList(id);
  },
  knownLang: () => knownLang(),
  nextCard: () => session.nextCard(),
  startDefi: () => defiController.startDefi(),
  renderUserBtn: () => statsUi.renderUserBtn(),
  setLocale: (loc) => setLocale(loc),
});

const defiController = createDefiController({
  getCards: () => state.cards,
  getDefi: () => state.defi,
  setDefi: (d) => {
    state.defi = d;
  },
  getSession: () => state.session,
  setSession: (s) => {
    state.session = s;
    // Every session reset (0,0) starts a new recap window — re-snapshot
    // so the next Terminer's deltas reflect only post-reset activity.
    // Cards may not be loaded yet on the first bootstrap call; guard.
    if (s.attempts === 0 && s.correct === 0 && state.cards.length) {
      captureSessionStartSnapshot();
    }
  },
  nextCard: () => session.nextCard(),
  clearPersistedDefi: () => clearDefiState(),
});

const sessionEnd = createSessionEnd({
  getState: () => state,
  getSessionStartSnapshot: () => sessionStartSnapshot,
  getList: () => getList(state.currentListId),
  onNewSession: () => {
    // "Nouvelle session" — zero counters, clear error list, re-snapshot
    // so the next recap's deltas reflect only post-restart activity.
    // Reprendre is a no-op (handled inside the controller) so the
    // session state stays intact on that path.
    state.session = { attempts: 0, correct: 0, errors: [] };
    captureSessionStartSnapshot();
    statsUi.renderStats();
    session.nextCard();
  },
  // Lambda — not eager — because deckStats is declared further down
  // the module. Click happens post-boot so the reference resolves.
  onOpenStats: () => deckStats.open(),
});

const wizard = createWizard({
  getTargetLang: () => state.targetLang,
  getSourceLang: () => state.sourceLang,
  getUserName: () => state.userName,
  getLevelFloor: () => state.levelFloor,
  setTargetLang: (lang) => {
    state.targetLang = lang;
  },
  setSourceLang: (lang) => {
    state.sourceLang = lang;
  },
  setUserName: (name) => {
    state.userName = name;
  },
  setLevelFloor: (lv) => {
    state.levelFloor = lv;
  },
  setUserLevel: (lv) => {
    state.userLevel = lv;
  },
  setDirRatio: (r) => {
    state.dirRatio = r;
  },
  setThemeId: (id) => {
    state.currentThemeId = id;
  },
  setListId: (id) => {
    state.currentListId = id;
  },
  applyLocale: () => applyLocale(),
  loadList: () => {
    loadList();
  },
});

// Deck settings modal — step 1b hosts direction / lang-pair /
// difficulty / tag filter; step 1c adds stats / mastery / TTS. Three
// entry points (⚙ under card, ⚙ hamburger, "niv" pill) all route
// through the same open() call so the modal is the one place
// deck-scoped preferences live.
const deckSettings = createDeckSettings({
  getList: () => getList(state.currentListId),
  renderLangControls: () => dropdowns.renderLangControls(),
  renderDiffDropdown: () => dropdowns.renderDiffDropdown(),
  renderTagFilterDropdown: () => dropdowns.renderTagFilterDropdown(),
  getTtsEnabled: () => state.ttsEnabled,
  setTtsEnabled: (v) => {
    state.ttsEnabled = v;
    saveTtsConfig(state.currentListId, {
      enabled: v,
      autoplay: state.ttsAutoplay,
    });
  },
  getTtsAutoplay: () => state.ttsAutoplay,
  setTtsAutoplay: (v) => {
    state.ttsAutoplay = v;
    saveTtsConfig(state.currentListId, {
      enabled: state.ttsEnabled,
      autoplay: v,
    });
  },
});

// Deck stats modal — per-deck DATA (stats grid, mastery histogram,
// CEFR level drill-down). Entry point: the "niv" header pill.
const deckStats = createDeckStats({
  getList: () => getList(state.currentListId),
  renderStats: () => statsUi.renderUserModalStats(),
  renderMasteryBar: () => statsUi.renderMasteryBar(),
  openLevelModal: () => statsUi.openLevelModal(),
});

// ---------------------------------------------------------------- locale / loadSheet

function setLocale(loc: string): void {
  if (!I18N[loc as Locale]) return;
  localStorage.setItem(STORAGE_KEYS.LOCALE, loc);
  // Auto-swap target if now conflicting with the new known lang — only
  // if source follows locale (i.e. sourceLang was never explicitly set).
  if (!state.sourceLang && state.targetLang === LOCALE_TO_LANG[loc as Locale]) {
    state.targetLang = defaultTargetFor(loc as Locale);
    localStorage.setItem(STORAGE_KEYS.TARGET, state.targetLang);
  }
  applyLocale();
  if (state.cards.length) session.nextCard();
}

/** Switch to a different list. Flushes any pending sync push so dirty
 *  keys from the outgoing deck reach the server before the local slice
 *  is swapped out, then resets session/filter transients, persists the
 *  new selection, swaps the stats slice, and reloads the new CSV.
 *
 *  Silently no-ops when the target id is the already-active list or
 *  unknown (getList normalizes, so "unknown == default" but that's
 *  always a valid switch). Safe to call while a sync is in flight —
 *  push() is awaited here. */
async function switchList(nextId: string): Promise<void> {
  const next = getList(nextId);
  if (next.id === state.currentListId) return;
  // Make the outgoing deck's dirty keys durable server-side before the
  // slice is replaced. If the user is offline or the webhook is down,
  // push() swallows the error and re-queues the keys; we proceed
  // regardless because we've already persisted the slice locally via
  // saveStats below, so a later push will still succeed once they're
  // online.
  if (sync.enabled()) await sync.push();
  // Persist the current slice in case a pending debounce hasn't fired
  // yet — prevents losing the last answer or two when switching fast.
  saveStats(state.currentListId, state.stats);
  // Swap slice + identity. Dirty set is per-slice (flat cardIds); a
  // fresh Set matches the new slice and avoids stale keys from the
  // outgoing deck crossing over. Theme follows the deck — every list
  // carries its themeId, so picking a deck in another theme via the
  // tabbed picker auto-updates `currentThemeId` without a separate
  // switchTheme() call. Single source of truth: the deck.
  state.currentListId = next.id;
  state.currentThemeId = next.themeId;
  localStorage.setItem(STORAGE_KEYS.LIST_ID, next.id);
  localStorage.setItem(STORAGE_KEYS.THEME_ID, next.themeId);
  state.stats = loadStats(next.id);
  state.dirty = new Set();
  // Restore the target deck's saved preferences — each deck remembers
  // its own dirRatio / difficulty / levelFloor so the user doesn't have
  // to retune the sliders every time they switch. Tag filter is the
  // exception: reset to empty on every deck switch so the user lands on
  // a fresh slate (carrying a filter across decks surprised users more
  // often than it helped).
  state.dirRatio = loadDirRatio(next.id);
  state.difficulty = loadDifficulty(next.id);
  state.levelFloor = loadLevelFloor(next.id);
  state.tagFilter = emptyTagFilter();
  saveTagFilter(next.id, state.tagFilter);
  const tts = loadTtsConfig(next.id, next.shape);
  state.ttsEnabled = tts.enabled;
  state.ttsAutoplay = tts.autoplay;
  state.session = { attempts: 0, correct: 0, errors: [] };
  state.defi = null;
  state.current = null;
  state.lastId = null;
  // A persisted défi belongs to the outgoing deck's cardId space —
  // keeping it would offer to "resume" a défi whose seenIds no longer
  // match anything in the new deck. Drop it.
  clearDefiState();
  dropdowns.renderTagFilterDropdown();
  dropdowns.renderListDropdown();
  await loadList();
  // Open deck settings right after the switch — every deck has its
  // own direction / difficulty / filter / TTS defaults, and the user
  // has just said "I'm starting this one". Showing the settings
  // surface turns the arrival into a moment to review/tweak before
  // playing, instead of hoping the defaults match their intent.
  // Only fires when the switch is user-initiated (via the deck
  // picker); the wizard's initial load goes through loadList() not
  // switchList() so first-time boot doesn't double-surface settings.
  deckSettings.open();
}

// switchTheme(themeId) used to live here; removed when the deck picker
// became theme-aware (tabs by theme inside the deck dropdown). The deck
// is now the single source of truth — switchList(deckId) keeps
// state.currentThemeId synced to the deck's themeId, so picking a deck
// in another theme via the tabs handles the theme switch implicitly.
// The wizard sets state directly via setThemeId/setListId during its
// commit, so no public switchTheme API is needed.

/** Snapshot the current in-memory défi into localStorage so a refresh
 *  can offer to resume it. No-op when not in défi mode or when no défi
 *  is active. Called from session.ts after each answer and after each
 *  nextCard pick. */
function persistCurrentDefi(): void {
  if (state.gameMode !== "defi" || !state.defi || state.defi.done) return;
  saveDefiState({
    listId: state.currentListId,
    total: state.defi.total,
    // Errors now live on session.errors — snapshot the slice into the
    // persisted défi so a resume restores the "à retravailler" list.
    errors: state.session.errors,
    seenIds: state.defi.seenIds ? [...state.defi.seenIds] : [],
    attempts: state.session.attempts,
    correct: state.session.correct,
    startedAt: state.defi.startedAt ?? Date.now(),
  });
}

/** Show the resume-in-progress-défi modal and wait for the user's
 *  choice. Returns "resume" or "new". Caller is responsible for
 *  actually rehydrating/clearing state based on the answer. */
function promptResumeDefi(done: number, total: number): Promise<"resume" | "new"> {
  return new Promise((resolve) => {
    const modal = byId("defiResumeModal");
    const title = byId("defiResumeTitle");
    const sub = byId("defiResumeSub");
    const continueBtn = byId<HTMLButtonElement>("defiResumeContinueBtn");
    const abandonBtn = byId<HTMLButtonElement>("defiResumeAbandonBtn");
    if (title) title.textContent = t("defi_resume_title") as string;
    if (sub) sub.textContent = t("defi_resume_sub", done, total) as string;
    if (continueBtn) continueBtn.textContent = t("defi_resume_continue") as string;
    if (abandonBtn) abandonBtn.textContent = t("defi_resume_abandon") as string;
    const close = (choice: "resume" | "new") => {
      hide(modal);
      // Clear listeners — the modal can be shown again on a future
      // reload, and stale listeners would double-resolve.
      continueBtn?.removeEventListener("click", onContinue);
      abandonBtn?.removeEventListener("click", onAbandon);
      resolve(choice);
    };
    const onContinue = () => close("resume");
    const onAbandon = () => close("new");
    continueBtn?.addEventListener("click", onContinue);
    abandonBtn?.addEventListener("click", onAbandon);
    show(modal);
  });
}

/** Toggle deck-specific UI affordances based on the active CardList's
 *  policy flags. Today: hide the "Ajouter" entry on curated factual
 *  decks where new entries arrive via curation, not user submissions
 *  (capitales, départements, …). Called whenever currentListId
 *  changes. */
function applyDeckUiPolicy(): void {
  const list = getList(state.currentListId);
  const allowAdd = list.allowAdd !== false; // default true
  addCardBtn.classList.toggle("hidden", !allowAdd);
}

async function loadList(): Promise<void> {
  clearLoadError();
  hide(cardScreen);
  show(loader);
  const list = getList(state.currentListId);
  // Persist the resolved id so a stale pointer normalizes to the
  // catalog's fallback on next boot.
  state.currentListId = list.id;
  localStorage.setItem(STORAGE_KEYS.LIST_ID, list.id);
  applyDeckUiPolicy();
  try {
    const text = await fetchSheetCsv(list.csvUrl);
    const cards = parseCards(text, list, currentLocale());
    state.cards = cards;
    hide(loader);
    show(cardScreen);
    show(userBtnEl);
    dropdowns.renderLangControls();
    if (state.gameMode === "defi" && !state.defi) {
      // Check for an in-progress défi belonging to this deck.
      const persisted = loadDefiState();
      const matches = persisted && persisted.listId === state.currentListId;
      if (matches) {
        const choice = await promptResumeDefi(
          persisted.attempts,
          persisted.total,
        );
        if (choice === "resume") {
          state.defi = {
            total: persisted.total,
            seenIds: new Set(persisted.seenIds),
            done: false,
            startedAt: persisted.startedAt || Date.now(),
          };
          // Errors moved to session.errors — restore them here so the
          // resumed défi's recap reflects the already-missed cards.
          state.session = {
            attempts: persisted.attempts,
            correct: persisted.correct,
            errors: persisted.errors,
          };
        } else {
          clearDefiState();
          state.defi = {
            total: GAME_MODES.defi.count,
            seenIds: new Set<string>(),
            done: false,
            startedAt: Date.now(),
          };
          state.session = { attempts: 0, correct: 0, errors: [] };
        }
      } else {
        // Persisted défi for a different deck — drop it, not
        // resumable. Start fresh.
        if (persisted) clearDefiState();
        state.defi = {
          total: GAME_MODES.defi.count,
          seenIds: new Set<string>(),
          done: false,
          startedAt: Date.now(),
        };
      }
    }
    // Capture session-start snapshot AND write today's progress
     // snapshot now that we have loaded stats for this deck. The
     // progress recorder is idempotent per local-day per deck, so
     // repeated boots in the same day are a no-op.
    captureSessionStartSnapshot();
    recordProgressSnapshot(state.currentListId, {
      masteredCount: sessionStartSnapshot!.masteredCount,
      userLevel: sessionStartSnapshot!.userLevel,
    });
    session.nextCard();
    if (sync.enabled()) sync.pull();
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    if (msg === "timeout") {
      showLoadError(t("load_timeout") as string);
    } else {
      showLoadError(t("load_failed") + msg);
    }
  }
}

async function detectInitialLocale(): Promise<Locale> {
  // 1) IP-based country lookup (api.country.is — tiny free service).
  try {
    const res = await Promise.race<Response>([
      fetch("https://api.country.is/", { cache: "no-store" }),
      new Promise<Response>((_, rej) =>
        setTimeout(() => rej(new Error("timeout")), 1500),
      ),
    ]);
    if (res && res.ok) {
      const data = await res.json();
      const country = String(data.country || "").toUpperCase();
      const loc = COUNTRY_TO_LOCALE[country];
      if (loc && I18N[loc]) return loc;
    }
  } catch {
    // fall through to browser hint
  }
  // 2) Browser language hint.
  try {
    const langs =
      navigator.languages && navigator.languages.length
        ? navigator.languages
        : [navigator.language || ""];
    for (const l of langs) {
      const short = String(l).toLowerCase().split("-")[0] as Locale;
      if (I18N[short]) return short;
    }
  } catch {
    // fall through
  }
  // 3) English fallback.
  return "en";
}

// ---------------------------------------------------------------- applyLocale

function applyLocale(): void {
  document.documentElement.lang = currentLocale();
  const setText = (id: string, key: string): void => {
    const el = byId(id);
    if (el) el.textContent = t(key) as string;
  };
  const setTitle = (id: string, key: string): void => {
    const el = byId(id);
    if (el) el.title = t(key) as string;
  };
  const setPh = (id: string, key: string): void => {
    const el = byId<HTMLInputElement>(id);
    if (el) el.placeholder = t(key) as string;
  };
  // Header
  const tagline = document.querySelector("header .sub");
  if (tagline) tagline.textContent = t("tagline") as string;
  setText("aideOverflowLabel", "aide_label");
  const aideBtnEl = byId("aideBtn");
  if (aideBtnEl) aideBtnEl.title = t("aide_label") as string;
  const themeToggleBtnEl = byId("themeToggleBtn");
  if (themeToggleBtnEl) {
    const isLight =
      document.documentElement.getAttribute("data-theme") === "light";
    themeToggleBtnEl.title = t(isLight ? "theme_to_dark" : "theme_to_light") as string;
  }
  setTitle("userLevelBtn", "level_pill_title");
  const userLevelLabel = document.querySelector("#userLevelBtn .level-pill-label");
  if (userLevelLabel) userLevelLabel.textContent = t("level_pill_label") as string;
  // Inline load-error block
  setText("loadRetryBtn", "load_retry");
  // Card screen
  setTitle("flagBtn", "flag_title");
  setTitle("skipBtn", "skip_title");
  setTitle("deckSettingsBtn", "deck_settings_btn_title");
  setTitle("deckSettingsMenuBtn", "deck_settings_btn_title");
  const submitBtnEl = byId("submitBtn");
  if (submitBtnEl) {
    submitBtnEl.textContent = t(state.phase === "answer" ? "btn_validate" : "btn_next") as string;
  }
  setText("dontKnowBtn", "btn_dont_know");
  setText("hintBtn", "btn_hint");
  setText("endSessionBtn", "btn_end_session");
  setText("loader", "loader");
  // Level modal
  const modalTitle = document.querySelector("#levelModal h2");
  if (modalTitle) modalTitle.textContent = t("modal_title") as string;
  const modalSub = document.querySelector("#levelModal .modal-sub");
  if (modalSub) modalSub.textContent = t("modal_subtitle") as string;
  setText("modalCloseBtn", "modal_close");
  // User modal
  setText("userModalTitle", "user_modal_title");
  setText("userModalNameLabel", "user_modal_name_label");
  // These labels now live inside deckSettingsModal (moved in 1b/1c)
  // but keep the same IDs so the same setText calls still find them.
  setText("userModalLearnLabel", "user_modal_learn_label");
  setText("userModalSourceLabel", "user_modal_source_label");
  setText("userModalStatsLabel", "user_modal_stats_label");
  // (userModalTtsLabel was renamed to deckTtsEnabledLabel +
  // deckTtsAutoplayLabel when the TTS config got split into master +
  // sub in 1c. Deck-settings' localizeInlineLabels handles them.)
  setText("fontSizeLabel", "font_size_label");
  setText("userModalLocaleLabel", "locale_label");
  setText("themeModalLabel", "theme_label");
  if (userModalCloseBtn) userModalCloseBtn.textContent = t("user_modal_close") as string;
  // Flag modal
  setText("flagModalTitle", "flag_modal_title");
  setText("flagModalSub", "flag_modal_sub");
  setText("flagModalReasonLabel", "flag_modal_reason");
  setText("flagModalSaveBtn", "flag_modal_save");
  setText("flagModalCancelBtn", "flag_modal_cancel");
  // Add-card modal
  setTitle("addCardBtn", "add_card_btn_title");
  setText("addCardOverflowLabel", "add_card_menu_label");
  setTitle("searchBtn", "search_btn_title");
  setText("searchOverflowLabel", "search_menu_label");
  setText("searchModalTitle", "search_title");
  setPh("searchInput", "search_placeholder");
  setText("searchModalCloseBtn", "search_close");
  setText("addCardModalTitle", "add_card_title");
  setText("addCardModalSub", "add_card_sub");
  setText("addCardModalTagsLabel", "add_card_tags_label");
  setPh("addCardModalTags", "add_card_tags_ph");
  setText("addCardModalLevelLabel", "add_card_level_label");
  setText("addCardModalSaveBtn", "add_card_save");
  setText("addCardModalCancelBtn", "add_card_cancel");
  setText("addCardModalSuggestLabel", "add_card_suggest");
  // Intro modal localization (even if closed — next open reads fresh text).
  localizeIntroModal();
  deckSettings.localize();
  deckStats.localize();
  statsUi.renderUserBtn();
  dropdowns.renderDiffDropdown();
  dropdowns.renderModeDropdown();
  dropdowns.renderTagFilterDropdown();
  dropdowns.renderListDropdown();
  // Mobile overflow menu labels. langOverflowLabel is a no-op today
  // (the lang dropdown has no header button) but kept wired for the
  // fallback branch. diff/tagFilter overflow labels were removed in 1b
  // when those controls moved into the deck-settings modal.
  setText("modeOverflowLabel", "mode_title");
  setText("langOverflowLabel", "menu_label_lang");
  setText("deckSettingsOverflowLabel", "menu_label_deck_settings");
  // Re-render dynamic blocks if a deck is already loaded.
  if (state.cards.length) {
    dropdowns.renderLangControls();
    statsUi.renderStats();
  }
}

// ---------------------------------------------------------------- event wiring

// Global click delegate so inline 🔊 buttons (emitted via innerHTML in
// card.ts and session.ts) just work without rebinding after each render.
installTtsDelegation();

loadRetryBtn?.addEventListener("click", () => {
  loadList();
});

submitBtn.addEventListener("click", () => session.submit());
dontKnowBtn.addEventListener("click", () => session.dontKnow());
hintBtn.addEventListener("click", () => session.hint());
skipBtn.addEventListener("click", () => session.nextCard());
flagBtn.addEventListener("click", () => cardModals.flagCurrentCard());

// Terminer la session — mode-dependent routing. Défi mode shows the
// (non-destructive) défi recap with a Reprendre button; libre /
// revision opens the session-end recap. No confirmation gate: the
// Reprendre affordance means nothing is lost in either case.
endSessionBtn?.addEventListener("click", () => {
  if (state.gameMode === "defi" && state.defi && !state.defi.done) {
    defiController.endDefi({ interrupted: true });
  } else {
    sessionEnd.open();
  }
});

// "niv" pill opens the deck-STATS modal — pill shows a CEFR level,
// which IS the tip of the stats iceberg for this deck. Keeps settings
// (configs) and stats (data) in separate surfaces to match user
// expectations ("settings" shouldn't contain read-only metrics).
byId("userLevelBtn")?.addEventListener("click", () => deckStats.open());
byId("modalCloseBtn")?.addEventListener("click", () => statsUi.closeLevelModal());
byId("levelModal")?.addEventListener("click", (e) => {
  if ((e.target as HTMLElement).id === "levelModal") statsUi.closeLevelModal();
});

// Deck settings entry points — two ways in, same open() call.
byId("deckSettingsBtn")?.addEventListener("click", () => deckSettings.open());
byId("deckSettingsMenuBtn")?.addEventListener("click", () => {
  closeHeaderMenu();
  deckSettings.open();
});
installDeckSettingsModal(() => deckSettings.close());
installDeckStatsModal(() => deckStats.close());

// Esc closes whichever modal is currently visible. Each modal owns its
// own close function (some have guard logic — e.g. userModal refuses to
// close with an incomplete language pair), so we dispatch by id rather
// than brute-force hide() everything. Ignored when no modal is open
// (the input keeps its Enter=submit shortcut regardless).
document.addEventListener("keydown", (e) => {
  if (e.key !== "Escape") return;
  const open = document.querySelector<HTMLElement>(".modal-overlay:not(.hidden)");
  if (!open) return;
  switch (open.id) {
    case "levelModal":
      statsUi.closeLevelModal();
      break;
    case "userModal":
      statsUi.closeUserModal();
      break;
    case "deckSettingsModal":
      deckSettings.close();
      break;
    case "deckStatsModal":
      deckStats.close();
      break;
    case "flagModal":
      cardModals.closeFlagModal();
      break;
    case "addCardModal":
      cardModals.closeAddCardModal();
      break;
    case "introModal":
      closeIntroModal();
      break;
    case "sessionEndModal":
      sessionEnd.close();
      break;
    case "searchModal":
      search.close();
      break;
    default:
      hide(open);
  }
});

answerInput.addEventListener("keydown", (e) => {
  if (e.key === "Enter") {
    e.preventDefault();
    session.submit();
  }
});

// Défi recap modal — 3 buttons, 2 visible at a time (see defi.ts for
// the swap logic). Reprendre closes without touching state (the défi
// is still in progress); Nouveau défi restarts; Mode libre exits to
// libre mode on natural completion.
byId("defiEndRestartBtn")?.addEventListener("click", () => {
  hide(byId("defiEndModal"));
  defiController.startDefi();
});
byId("defiEndBackBtn")?.addEventListener("click", () => {
  hide(byId("defiEndModal"));
  dropdowns.switchGameMode("libre");
});
byId("defiEndResumeBtn")?.addEventListener("click", () => {
  hide(byId("defiEndModal"));
});
byId("defiEndModal")?.addEventListener("click", (e) => {
  // Backdrop click = Reprendre (no state change) for interrupted
  // recaps, same effect as closing the modal for completed ones.
  if ((e.target as HTMLElement).id === "defiEndModal") {
    hide(byId("defiEndModal"));
  }
});

// Profile modal
userBtnEl?.addEventListener("click", () => statsUi.openUserModal());
userModalCloseBtn?.addEventListener("click", () => statsUi.closeUserModal());
userModal?.addEventListener("click", (e) => {
  if (e.target === userModal) statsUi.closeUserModal();
});
// Replay the guided tour from the "aide" item in the hamburger menu.
// The menu-level outside-click handler closes the popover on any
// external click — stopping propagation here so our own click doesn't
// race with the menu close would be brittle; instead we just close
// the menu explicitly first and open the tour after a tick so the
// tour's resize/target resolution sees a settled DOM.
byId("aideBtn")?.addEventListener("click", (e) => {
  e.stopPropagation();
  closeHeaderMenu();
  requestAnimationFrame(() => openTour());
});

// Flag/correct modal
byId("flagModalSaveBtn")?.addEventListener("click", () => cardModals.saveFlagModal());
byId("flagModalCancelBtn")?.addEventListener("click", () => cardModals.closeFlagModal());
byId("flagModal")?.addEventListener("click", (e) => {
  if ((e.target as HTMLElement).id === "flagModal") cardModals.closeFlagModal();
});

// Add-card modal
addCardBtn.addEventListener("click", () => cardModals.openAddCardModal());
const search = createSearch({
  getCards: () => state.cards,
  getDirection: () => state.direction,
  getList: () => getList(state.currentListId),
  goToCard: (card) => session.goToCard(card),
});
searchBtn.addEventListener("click", () => search.open());
byId("addCardModalSuggestBtn")?.addEventListener("click", () =>
  cardModals.suggestAddCardModal(),
);
byId("addCardModalSaveBtn")?.addEventListener("click", () =>
  cardModals.saveAddCardModal(),
);
byId("addCardModalCancelBtn")?.addEventListener("click", () =>
  cardModals.closeAddCardModal(),
);
byId("addCardModal")?.addEventListener("click", (e) => {
  if ((e.target as HTMLElement).id === "addCardModal") cardModals.closeAddCardModal();
});

// Flush any pending dirty stats on tab close / bfcache transition.
// `pagehide` is preferred over `beforeunload` — it fires reliably on
// mobile Safari and for back-forward cache entries.
window.addEventListener("pagehide", () => sync.flushBeacon());

// ---------------------------------------------------------------- boot

// Cleanup of legacy localStorage keys from when the sheet URL + webhook
// credentials were user-configurable. Silent — these values are now
// baked into the bundle (see config/lists.ts + config/defaults.ts).
for (const key of LEGACY_STORAGE_KEYS) localStorage.removeItem(key);

(async function init(): Promise<void> {
  applyLocale();
  // Backward-compat: existing users (with locale AND name already set)
  // skip the wizard. Users who never gave a name still need to go
  // through it so cross-device stats sync can identify them.
  if (
    localStorage.getItem(STORAGE_KEYS.WIZARD) !== "1" &&
    localStorage.getItem(STORAGE_KEYS.LOCALE) &&
    (localStorage.getItem(STORAGE_KEYS.USER) || "").trim()
  ) {
    localStorage.setItem(STORAGE_KEYS.WIZARD, "1");
  }
  const wizardDone =
    localStorage.getItem(STORAGE_KEYS.WIZARD) === "1" &&
    (localStorage.getItem(STORAGE_KEYS.USER) || "").trim();
  if (!wizardDone) {
    if (!localStorage.getItem(STORAGE_KEYS.LOCALE)) {
      const detected = await detectInitialLocale();
      localStorage.setItem(STORAGE_KEYS.LOCALE, detected);
      applyLocale();
    }
    // HTML ships with #loader visible by default (so slow mobile boots
    // see "chargement…" instead of a blank stage). Hide it here before
    // handing off to the intro/wizard path, which shows its own section.
    hide(loader);
    // Show the intro screen first so the user learns what the app is
    // (in their likely language) before we throw profile questions at
    // them. Locale can still be overridden on the intro screen. Then
    // the wizard runs. Tour skips its own intro step on this path
    // since the intro screen already covered that ground.
    showIntroScreen({
      applyLocale,
      onContinue: () => wizard.startWizard(),
    });
    return;
  }
  loadList();
})();
